/**
 * PricingPlans — post-payments onboarding step.
 *
 * Handoff order at end-of-onboarding:
 *   Onboarding.finish()  →  /welcome            (housekeeping toggles)
 *                       →  /welcome/payments    (Get Paid Faster wizard)
 *                       →  /welcome/summary     (celebrate)
 *                       →  /welcome/pricing     (this page)
 *                       →  /accounting/transactions?from=onboarding (land)
 *
 * Visual language mirrors the "Get Paid Faster" step — same slate→white
 * ambient gradient, same rounded-3xl hero card, same eyebrow + bold
 * title header slot. Center card is glow-highlighted as MOST POPULAR
 * to nudge the plan we actually want people on.
 *
 * Billing cadence toggle lives at the top-right; default is ANNUAL so
 * the two-months-free savings show up in the first eyeful. Switching
 * to monthly recalculates each card's headline number in-place.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  Check, Star, ArrowRight, Crown, Loader2, Sparkles,
} from "lucide-react";
import { useBranding } from "@/lib/branding";
import { useCompany } from "@/lib/company";
import { CompanySwitcher, ProfileMenu } from "@/components/Layout";
import { api } from "@/lib/api";

// ─── Plan catalog ──────────────────────────────────────────────────
// One entry per tier. `monthly` is the sticker price when billed
// monthly; `annual` is the full-year charge for the same tier. The
// "effective monthly" number you see on the annual toggle is derived
// from `annual / 12` — never hardcoded, so a single number change to
// `annual` keeps the whole card in sync.
const PLANS = [
  {
    id:       "core",
    name:     "Core",
    tagline:  "Smarter accounting. Less work.",
    monthly:  38,
    annual:   380,
    seatCopy: "1 Company · 1 User + Accountant · 3 Connected Accounts",
    highlight: false,
    // Wired to Stripe: matches STRIPE_PRICE_SIMPLE_START_MONTHLY_38
    // via the backend `_price_id("simple_start", discount=False)`
    // resolver. Clicking "Choose Core" opens a Stripe Checkout
    // session with a 7-day free trial attached.
    stripeProduct: "simple_start",
    trialDays: 7,
    features: [
      { h: "Normal Accounting",
        b: "Everything a real ledger needs — accrual + cash." },
      { h: "AI Categorization",
        b: "Automatically categorizes transactions." },
      { h: "AI Revenue Recognition",
        b: "Identifies revenue transactions and helps ensure they're recorded correctly." },
      { h: "AI Internal Transfer Recognition",
        b: "Recognizes transfers between connected accounts to avoid duplicate income or expenses." },
      { h: "AI Contact Recognition",
        b: "Identifies the actual customer or vendor behind transactions, including payments through services like Zelle." },
    ],
    bestFor: "Small businesses that want straightforward accounting with AI automatically handling the repetitive work.",
  },
  {
    id:       "assistant",
    name:     "AI Assistant",
    tagline:  "Ask. Review. Get it done.",
    monthly:  79,
    annual:   790,
    seatCopy: "1 Company · 3 Users + Accountant · 6 Connected Accounts",
    highlight: false,
    // Wired to Stripe: STRIPE_PRICE_ASSISTANT_{MONTHLY|ANNUAL}.
    stripeProduct: "assistant",
    trialDays: 7,
    features: [
      { h: "Everything in Core, plus", isSection: true },
      { h: "AI Review Chat",
        b: "Ask questions and review your books directly with your AI assistant." },
      { h: "AI Reconciliation",
        b: "Helps reconcile accounts and identify discrepancies." },
      { h: "AI Navigation",
        b: "Tell the AI where you want to go instead of searching through menus." },
      { h: "AI Commands",
        b: "Ask the AI to perform accounting tasks for you." },
      { h: "AI Receipt Processing",
        b: "Upload receipts and let AI extract and organize the accounting information." },
      { h: "AI Financial Outlook",
        b: "See a plain-English readout of where the business is trending." },
      { h: "Cash, Burn & Runway",
        b: "Track cash on hand, monthly burn, and how long the runway lasts." },
      { h: "Projections",
        b: "Forward-looking cash and P&L projections built from your data." },
    ],
    bestFor: "Businesses that want powerful AI tools to understand their finances, review their books, and get accounting work done faster.",
  },
  {
    id:       "bookkeeper",
    name:     "AI Bookkeeper",
    tagline:  "We handle the books. You run the business.",
    monthly:  99,
    annual:   990,
    seatCopy: "1 Company · 5 Users + Accountant · Unlimited Connected Accounts",
    highlight: true,
    // Wired to Stripe: STRIPE_PRICE_BOOKKEEPER_{MONTHLY|ANNUAL}.
    stripeProduct: "bookkeeper",
    trialDays: 7,
    features: [
      { h: "Everything in AI Assistant, plus", isSection: true },
      { h: "AI Proactive Check-ins",
        b: "Proactively reaches out when information, clarification, or action is needed." },
      { h: "AI Bookkeeper Review",
        b: "Reviews your books for potential errors, unusual activity, and accounting issues." },
      { h: "AI Month-End Close",
        b: "Runs a guided monthly close so the books are always current." },
      { h: "AI Accuracy Review",
        b: "Cross-checks entries against source data and flags anything off." },
      { h: "AI Anomaly Detection",
        b: "Surfaces unusual transactions before they become a mess later." },
      { h: "Liability AI",
        b: "Upload liability information and automatically split payments between principal, interest, insurance, escrow, and other components." },
      { h: "AI Bank Statement Processing",
        b: "Upload bank statements for AI-assisted processing and review." },
      { h: "Classes",
        b: "Track income and expenses across different areas of the business." },
      { h: "AI Automations",
        b: "Set-and-forget rules the AI executes when conditions are met." },
    ],
    bestFor: "Businesses that want the bookkeeping handled for them, with AI proactively managing, reviewing, and closing the books — and involving the business owner only when needed.",
  },
  {
    id:       "advanced",
    name:     "Advanced",
    tagline:  "More complexity. Still handled.",
    monthly:  149,
    annual:   1490,
    seatCopy: "1 Company · 5 Users + Accountant · Unlimited Connected Accounts",
    highlight: false,
    // Wired to Stripe: STRIPE_PRICE_ADVANCED_{MONTHLY|ANNUAL}.
    stripeProduct: "advanced",
    trialDays: 7,
    features: [
      { h: "Everything in AI Bookkeeper, plus", isSection: true },
      { h: "AI Bill Processing",
        b: "Scan and process bills with AI." },
      { h: "Advanced AI Financial Insights",
        b: "Deeper pattern analysis across trends, changes, and opportunities." },
      { h: "Advanced Forecasting",
        b: "Multi-scenario forecasting with what-if adjustments." },
      { h: "Budgeting",
        b: "Build and track budgets against actual performance." },
      { h: "Inventory",
        b: "Track inventory and its accounting impact." },
      { h: "Sales Tax Tracking",
        b: "Track sales tax collected and amounts owed." },
      { h: "Employee Reimbursement Tracking",
        b: "Track employee expenses and reimbursements." },
    ],
    bestFor: "Growing and more complex businesses that need advanced accounting, planning, inventory, sales tax, and operational financial tools.",
  },
];

// Currency helper — pinned to en-US since the app's revenue side is
// USD-first. Uses cents when the number has a fractional part, whole
// dollars otherwise so headline numbers stay clean ($38, not $38.00).
const money = (n) => {
  const rounded = Math.round(n * 100) / 100;
  const hasCents = Math.abs(rounded % 1) > 0.001;
  return `$${rounded.toLocaleString("en-US", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};


// Pricing is the last onboarding step — land in the review queue.
const NEXT_AFTER_PRICING = "/accounting/transactions?from=onboarding";

// Staff bypass behind the logo: only the SHA-256 of the code ships to
// the browser, never the digits themselves.
const BYPASS_HASH = "5590a50fcb0ec10eb2c64d23996be0904ea6de9cde64f2ff708e0aae26005722";
async function bypassMatches(code) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode((code || "").trim()));
  const hex = [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
  return hex === BYPASS_HASH;
}

export default function PricingPlans() {
  const nav = useNavigate();
  // Read the current pro/firm branding so we can pin the firm logo
  // in the top-left corner (this page sits OUTSIDE the shared Layout
  // which normally shows the logo in the sidebar). Falls through
  // gracefully when a firm hasn't uploaded a logo yet.
  const { branding } = useBranding() || {};
  const logos = branding?.logos || {};
  const logoUrl = logos.logo_light || logos.icon_light
                  || branding?.logo_data_url || null;

  // Which company we're subscribing on behalf of. Comes from the
  // shared company context (same source of truth every other billing
  // caller uses — see BillingLockedModal / BillingReturn). Onboarding
  // reaches this page AFTER company creation, so `currentId` should
  // always be set; we still guard against it below and surface a
  // toast if it's missing rather than silently no-op.
  const { currentId, companies } = useCompany();

  // Annual is the recommended default — it's the plan we WANT people
  // on (better retention, cheaper to serve monthly infra). Sits atop
  // page so the two-months-free savings show up in the first eyeful.
  const [cadence, setCadence] = useState("annual"); // "monthly" | "annual"

  // Feature detail toggle — the sub-line under each bullet (e.g.
  // "Ask questions and review your books directly with your AI
  // bookkeeper.") gets noisy when scanned side-by-side. Default is
  // OFF so the cards read like a comparison surface first; the pill
  // to the right of the cadence toggle expands them for readers who
  // want the full pitch.
  const [showDetail, setShowDetail] = useState(false);

  // Tracks which plan is currently redirecting to Stripe so the
  // matching card's CTA can flip to a spinner and disable itself.
  // Cleared once we hand off to `window.location` (which will unmount
  // this page anyway) or on error.
  const [loadingPlanId, setLoadingPlanId] = useState(null);

  // Land at the top of the page whenever we mount. Without this, a
  // user arriving from `/welcome/payments` (which they've usually
  // scrolled well down) inherits the previous scroll offset and
  // opens this step mid-page. `"auto"` (not smooth) so the reset is
  // invisible — feels like a fresh page load, not a jump animation.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  // Enterprise-sponsored companies ("Enterprise pays" / "Free enterprise
  // spot") never pick a plan — their billing is already settled by the
  // firm. Skip straight to the summary. Hold rendering until we know.
  const [sponsored, setSponsored] = useState(null); // null = checking
  // Returning customer (had a subscription before) → no second trial,
  // "welcome back" copy, previous plan highlighted.
  const [returning, setReturning] = useState(null); // {previous_product, previous_plan_label, canceled_at} | null
  const [codeOpen, setCodeOpen] = useState(false);
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState(false);
  // Affiliate custom pricing — when set, the grid collapses to a single
  // Advanced card at the affiliate's price (see routes/affiliate_pricing.py).
  const [customPricing, setCustomPricing] = useState(null);
  useEffect(() => {
    if (!currentId) { setSponsored(false); return; }
    let cancelled = false;
    api.get(`/companies/${currentId}/billing/state`)
      .then((r) => {
        if (cancelled) return;
        const payer = r.data?.billing_payer;
        const state = r.data?.billing_state;
        const isSponsored = ["enterprise", "free_spot", "investor"].includes(payer);
        // A canceled/past-due sub still carries its old stripe_subscription_id,
        // so only a live state counts as paid (otherwise we'd bounce back to
        // the ledger, where the lock modal sends us here again — strobe loop).
        // Only a LIVE Stripe subscription counts as "already paid". A company
        // marked active without a subscription (manual override, comped) still
        // needs Checkout, so it must see the plans. Active subscribers who land
        // here go to Billing, where the Stripe portal handles plan changes.
        const alreadyPaid = !!r.data?.stripe_subscription_id
          && (state === "active" || !["canceled", "past_due", "unpaid", "pending"].includes(state));
        if (r.data?.custom_pricing) {
          setCustomPricing(r.data.custom_pricing);
          setCadence(r.data.custom_pricing.cadence === "annual" ? "annual" : "monthly");
        }
        if (r.data?.trial_eligible === false) {
          setReturning({ previous_product: r.data.previous_product, previous_plan_label: r.data.previous_plan_label, canceled_at: r.data.canceled_at,
                         canceled: state === "canceled" || !!r.data.canceled_at });
        }
        setSponsored(isSponsored || alreadyPaid);
        if (isSponsored) nav(NEXT_AFTER_PRICING, { replace: true });
        else if (alreadyPaid) nav("/billing", { replace: true });
      })
      .catch(() => { if (!cancelled) setSponsored(false); });
    return () => { cancelled = true; };
  }, [currentId, nav]);

  // Kick off a Stripe Checkout session for the selected plan and
  // redirect the browser to the returned URL. Only plans that have
  // `stripeProduct` set are wired — the others fall through to the
  // legacy nav('/welcome/summary') path until their price IDs land.
  //
  // We deliberately use `window.location.href` (never `nav()`) so the
  // browser fully leaves the SPA and hands off to Stripe's hosted
  // Checkout page — same pattern used by BillingLockedModal.
  const startCheckout = async (plan) => {
    // Un-wired plan → keep the current soft-continue behavior so we
    // don't break the onboarding flow while other price IDs are still
    // pending.
    if (!plan?.stripeProduct) {
      nav(NEXT_AFTER_PRICING);
      return;
    }
    if (!currentId) {
      toast.error("Couldn't find your company yet — please refresh and try again.");
      return;
    }
    setLoadingPlanId(plan.id);
    try {
      const r = await api.post(`/companies/${currentId}/billing/checkout-session`, {
        product: plan.stripeProduct,
        cadence,                                  // "monthly" | "annual" — from cadence toggle
        origin_url: window.location.origin,
        trial_period_days: returning ? undefined : (plan.trialDays || undefined),
        custom_pricing: plan.custom ? true : undefined,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;                    // page will unmount on redirect
      }
      throw new Error("No checkout URL in response");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Couldn't start checkout";
      toast.error(msg);
      setLoadingPlanId(null);
    }
  };

  // Footer "Continue" / "Not right now" — always skip to summary.

  if (sponsored !== false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center" data-testid="pricing-plans-checking">
        <Loader2 size={22} className="animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white p-6 pt-14" data-testid="pricing-plans-page">
      {/* Local toaster — this page sits OUTSIDE the shared Layout so
          the global <Toaster/> in Layout isn't mounted here. Without
          this local copy, any sonner toast() call from this page (or
          a child) would silently no-op. */}
      <Toaster richColors position="top-center" />

      {/* Top-left firm logo — pinned so a chrome-less page still
          carries the brand mark. Clicking it reveals a hidden passcode
          field (staff bypass) that skips the plan picker. */}
      {(
        <div className="fixed top-5 left-6 z-10 flex items-center gap-3">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Firm logo"
              onClick={() => { setCodeOpen((v) => !v); setCode(""); setCodeErr(false); }}
              className="h-9 w-auto max-w-[180px] object-contain cursor-pointer select-none"
              data-testid="pricing-firm-logo"
            />
          ) : (
            <button
              type="button"
              onClick={() => { setCodeOpen((v) => !v); setCode(""); setCodeErr(false); }}
              className="flex items-center gap-2 select-none"
              aria-label="Brand"
              data-testid="pricing-firm-logo"
            >
              <span className="w-9 h-9 rounded-lg bg-blue-600 grid place-items-center"><Sparkles size={18} className="text-white" /></span>
              <span className="font-heading font-bold text-base text-slate-900">{branding?.app_name || branding?.firm_name || "SmartBooks"}</span>
            </button>
          )}
          {codeOpen && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (await bypassMatches(code)) { nav(NEXT_AFTER_PRICING); return; }
                setCodeErr(true); setCode("");
              }}
              className="flex items-center gap-1.5"
              data-testid="pricing-bypass-form"
            >
              <input
                type="password"
                inputMode="numeric"
                autoFocus
                value={code}
                onChange={(e) => { setCode(e.target.value); setCodeErr(false); }}
                placeholder="••••••"
                className={`w-28 h-9 px-3 rounded-lg border text-sm tracking-[0.35em] bg-white/90 backdrop-blur focus:outline-none focus:ring-2 ${codeErr ? "border-rose-300 focus:ring-rose-200" : "border-slate-200 focus:ring-slate-300"}`}
                aria-label="Access code"
                data-testid="pricing-bypass-input"
              />
              <button type="submit" className="h-9 px-3 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800" data-testid="pricing-bypass-submit">
                Go
              </button>
            </form>
          )}
          {companies?.length > 1 && (
            <div className="flex items-center gap-3 pl-3 border-l border-slate-200" data-testid="pricing-entity-picker">
              <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold hidden sm:inline">Plan for</span>
              <CompanySwitcher />
            </div>
          )}
        </div>
      )}

      {/* Top-right profile chip — gives a locked-out / returning
          customer a way to switch accounts or sign out. */}
      <div className="fixed top-4 right-6 z-10" data-testid="pricing-profile-menu">
        <ProfileMenu />
      </div>

      <div className="max-w-[1500px] mx-auto">

        {/* Centered marketing header — crown, headline, feature-chip
            row, and cadence toggle stack on the same axis. Replaces
            the old eyebrow/title/toggle grid so the whole page reads
            like a landing surface, not an admin panel step. `current`
            (company) is intentionally NOT rendered here; a signed-in
            trial starter doesn't need to be reminded which company
            they're setting up mid-onboarding. */}
        <div className="flex flex-col items-center text-center mb-10">
          <div
            className="mb-4 w-11 h-11 rounded-full inline-flex items-center justify-center bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-200 shadow-sm"
            data-testid="pricing-crown"
          >
            <Crown size={20} className="text-amber-500" fill="currentColor" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight" data-testid="pricing-headline">
            {customPricing
              ? (customPricing.trial && !returning ? `Your plan is ready — start your ${customPricing.trial_days}-day free trial` : "Your plan is ready")
              : returning ? (returning.canceled ? "Welcome back — pick up where you left off." : "Choose your plan") : "Select a plan to start your 7-day free trial"}
          </h1>
          {customPricing && (
            <p className="mt-2 text-sm text-slate-600 max-w-xl" data-testid="pricing-custom-sub">
              {customPricing.affiliate_name ? <><b>{customPricing.affiliate_name}</b> arranged </> : "You've been set up with "}
              the <b>{customPricing.tier_label}</b> plan at a custom rate of <b>${(customPricing.monthly_cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}/mo</b>
              {cadence === "annual" ? <> — or <b>${(customPricing.annual_cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}/year</b> (2 months free)</> : null}.
            </p>
          )}
          {returning && (
            <p className="mt-2 text-sm text-slate-600 max-w-xl" data-testid="pricing-returning-sub">
              {returning.previous_plan_label && returning.canceled ? <>Your <b>{returning.previous_plan_label}</b> plan was canceled{returning.canceled_at ? ` on ${new Date(returning.canceled_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}. Reactivate it or choose a different plan — billing starts today.</>
                : returning.previous_plan_label ? <>You're currently on <b>{returning.previous_plan_label}</b>. Pick the plan that fits — billing starts today.</>
                : <>Choose a plan — billing starts today.</>}
            </p>
          )}

          {/* Feature pills — soft chips that surface the "why it's
              safe to click" signals without cluttering the plan cards
              themselves. Wrap gracefully at narrower breakpoints. */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {(returning ? [
              "Your books and history are exactly as you left them",
              "98% Auto-categorization",
              "Full service accounting option (via partners)",
            ] : [
              "7-day free trial",
              "98% Auto-categorization",
              "Full service accounting option (via partners)",
              "Cancel anytime",
            ]).map((chip) => (
              <span
                key={chip}
                className="inline-flex items-center rounded-full border border-slate-200 bg-white/80 px-3.5 py-1.5 text-[11px] font-medium text-slate-700 shadow-sm"
                data-testid={`pricing-chip-${chip.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/(^-|-$)/g,'')}`}
              >
                {chip}
              </span>
            ))}
          </div>

          {/* Cadence toggle — centered below chips. The "2 Months
              Free" chip inside the annual pill replaces the old
              separate "Annual billing: Get 2 months free" ribbon so
              this header stays a single tight column. The
              detail-toggle pill sits to its right so both live in
              one visual axis. */}
          <div className="mt-5 flex items-center justify-center gap-3">
            <CadenceToggle cadence={cadence} onChange={setCadence} />
            <button
              type="button"
              onClick={() => setShowDetail((v) => !v)}
              aria-pressed={showDetail}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition border-2 border-slate-900 shadow-sm ${
                showDetail
                  ? "bg-emerald-700 text-white hover:bg-emerald-800"
                  : "bg-emerald-500 text-white hover:bg-emerald-600"
              }`}
              data-testid="pricing-detail-toggle"
            >
              {showDetail ? "Hide Detail" : "Show Detail"}
            </button>
          </div>
        </div>

        {/* Plan grid — 1-column on mobile, 3-column at ≥lg. Middle
            card scales up 2% at ≥lg so the eye lands there first. */}
        {/* Plan grid — 1-column on mobile, 2-column at md, 4-column
            at ≥lg so the four plans stay in a single row on any
            reasonable desktop viewport. AI Bookkeeper card scales up
            2% at ≥lg so the eye lands there first. */}
        {customPricing ? (
          <CustomPlanLayout
            plan={customPlan(customPricing)}
            cadence={cadence}
            showDetail={showDetail}
            loading={loadingPlanId === "custom"}
            onSelect={startCheckout}
          />
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-5 items-stretch">
          {PLANS.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              cadence={cadence}
              showDetail={showDetail}
              loading={loadingPlanId === p.id}
              anyLoading={loadingPlanId !== null}
              onSelect={startCheckout}
              returning={returning}
            />
          ))}
        </div>
        )}

      </div>
    </div>
  );
}

const CUSTOM_GROUP_LABELS = {
  core: "Accounting",
  assistant: "AI Assistant",
  bookkeeper: "Books on Autopilot",
  advanced: "Advanced Tools",
};

function customPlan(cp) {
  const tier = cp.tier || "advanced";
  const idx = PLANS.findIndex((p) => p.stripeProduct === tier);
  const base = idx >= 0 ? PLANS[idx] : PLANS[PLANS.length - 1];
  const chain = PLANS.slice(0, (idx >= 0 ? idx : PLANS.length - 1) + 1);
  const groups = chain.map((p) => ({
    id: p.id,
    label: CUSTOM_GROUP_LABELS[p.id] || p.name,
    items: p.features.filter((f) => !f.isSection),
  }));
  return {
    ...base,
    id: "custom",
    name: cp.tier_label || base.name,
    tagline: cp.affiliate_name ? `Custom rate arranged by ${cp.affiliate_name}` : "Your custom rate",
    monthly: cp.monthly_cents / 100,
    annual: cp.annual_cents / 100,
    highlight: true,
    trialDays: cp.trial ? cp.trial_days : 0,
    custom: true,
    groups,
    features: groups.flatMap((g) => g.items),
  };
}

function CustomPlanLayout({ plan, cadence, showDetail, loading, onSelect }) {
  const headlinePrice = cadence === "annual" ? plan.annual / 12 : plan.monthly;
  const hasTrial = !!plan.trialDays;
  return (
    <div className="space-y-6" data-testid="pricing-custom-grid">
      <div className="relative rounded-3xl bg-slate-900 text-white border border-emerald-400/40 shadow-lg shadow-emerald-900/30" data-testid="pricing-card-custom">
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <div className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 shadow-lg">
            <Star size={10} fill="currentColor" /> Your plan
          </div>
        </div>
        <div className="p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
          <div className="flex-1">
            <div className="text-xl font-bold">{plan.name}</div>
            <div className="text-[13px] mt-1 text-emerald-100/80">{plan.tagline}</div>
            {hasTrial && (
              <div className="mt-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest bg-emerald-400/15 text-emerald-200 border border-emerald-400/30" data-testid="pricing-trial-custom">
                <Star size={9} fill="currentColor" /> {plan.trialDays}-day free trial
              </div>
            )}
            <div className="mt-4 text-[11px] uppercase tracking-widest font-semibold text-emerald-100/70" data-testid="pricing-seat-copy-custom">
              {plan.seatCopy}
            </div>
          </div>
          <div className="md:text-right md:min-w-[280px]">
            <div className="flex items-end gap-1.5 md:justify-end">
              <span className="text-4xl sm:text-5xl font-extrabold tracking-tight tabular-nums">{money(headlinePrice)}</span>
              <span className="pb-1.5 text-sm font-medium text-emerald-100/70">/mo</span>
            </div>
            <div className="mt-1 text-[11px] text-emerald-100/60">
              {cadence === "annual" ? <>Billed <b>{money(plan.annual)}</b>/year · 2 months free</> : <>Billed monthly · switch to annual anytime</>}
            </div>
            <button
              type="button"
              onClick={() => onSelect(plan)}
              disabled={loading}
              className="mt-4 w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-full px-8 py-3 text-sm font-bold shadow-md transition-transform hover:shadow-lg hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
              data-testid="pricing-select-custom"
            >
              {loading ? <><Loader2 size={14} className="animate-spin" /> Redirecting to Stripe…</>
                : hasTrial ? <>Start {plan.trialDays}-day free trial <ArrowRight size={14} /></>
                : <>Get started <ArrowRight size={14} /></>}
            </button>
          </div>
        </div>
      </div>

      <div className="text-center text-[11px] uppercase tracking-widest font-bold text-emerald-700">Everything included</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
        {plan.groups.map((g) => (
          <div key={g.id} className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6" data-testid={`pricing-custom-group-${g.id}`}>
            <div className="text-sm font-bold text-slate-900 mb-4">{g.label}</div>
            <ul className="space-y-3">
              {g.items.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <div className="mt-0.5 shrink-0 w-4 h-4 rounded-full inline-flex items-center justify-center bg-emerald-100 text-emerald-600">
                    <Check size={11} strokeWidth={3} />
                  </div>
                  <div>
                    <div className="text-[13px] font-semibold text-slate-800">{f.h}</div>
                    {f.b && showDetail && <div className="text-[11px] leading-relaxed mt-0.5 text-slate-500">{f.b}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}


/**
 * CadenceToggle — top-right pill that flips between Monthly & Annual
 * billing. A tiny "Save 2 months" chip on the annual side signals the
 * discount without overloading the header.
 */
function CadenceToggle({ cadence, onChange }) {
  return (
    <div
      className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 p-1"
      role="tablist"
      data-testid="pricing-cadence-toggle"
    >
      <button
        type="button"
        role="tab"
        aria-selected={cadence === "monthly"}
        onClick={() => onChange("monthly")}
        className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${
          cadence === "monthly"
            ? "bg-white text-slate-900 shadow-sm"
            : "text-slate-500 hover:text-slate-700"
        }`}
        data-testid="pricing-cadence-monthly"
      >
        Monthly
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={cadence === "annual"}
        onClick={() => onChange("annual")}
        className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition ${
          cadence === "annual"
            ? "bg-white text-slate-900 shadow-sm"
            : "text-slate-500 hover:text-slate-700"
        }`}
        data-testid="pricing-cadence-annual"
      >
        Annual
        <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${
          cadence === "annual"
            ? "bg-emerald-100 text-emerald-700"
            : "bg-emerald-50 text-emerald-600"
        }`}>
          2 Months Free
        </span>
      </button>
    </div>
  );
}


/**
 * PlanCard — one tier. Middle card gets the popular treatment:
 *   * emerald→indigo gradient ring around the outside
 *   * "Most Popular" badge floating above the top edge
 *   * slight scale bump at ≥lg so the eye lands there first
 *   * dark card body with white text
 */
function PlanCard({ plan, cadence, showDetail, loading, anyLoading, onSelect, returning }) {
  const isPrevious = !!returning && returning.previous_product === plan.stripeProduct;
  const headlinePrice = useMemo(() => {
    return cadence === "annual" ? plan.annual / 12 : plan.monthly;
  }, [cadence, plan]);

  const popular = plan.highlight;
  const hasTrial = !!plan.trialDays && !returning;
  const wired    = !!plan.stripeProduct;

  return (
    <div
      className={`relative flex flex-col rounded-3xl shadow-lg transition-transform ${
        popular
          ? "lg:scale-[1.02] bg-slate-900 text-white border border-emerald-400/40 shadow-emerald-900/30"
          : "bg-white text-slate-900 border border-slate-200"
      }`}
      data-testid={`pricing-card-${plan.id}`}
    >
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <div className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 shadow-lg">
            <Star size={10} fill="currentColor" /> {plan.custom ? "Your plan" : "Most Popular"}
          </div>
        </div>
      )}

      <div className="p-6 sm:p-7">
        <div className={`text-base font-bold ${popular ? "text-white" : "text-slate-900"}`}>
          {plan.name}
        </div>
        <div className={`text-[13px] mt-1 ${popular ? "text-emerald-100/80" : "text-slate-500"}`}>
          {plan.tagline}
        </div>

        {/* Trial ribbon — only rendered when the plan actually carries
            a trial. Sits above the price so the "free" beat lands
            before the dollar amount does. */}
        {isPrevious && (
          <div className={`mt-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${popular ? "bg-white/15 text-white border border-white/30" : "bg-slate-100 text-slate-700 border border-slate-200"}`} data-testid={`pricing-previous-${plan.id}`}>
            {returning.canceled ? "Your previous plan" : "Your current plan"}
          </div>
        )}
        {hasTrial && (
          <div
            className={`mt-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
              popular
                ? "bg-emerald-400/15 text-emerald-200 border border-emerald-400/30"
                : "bg-emerald-50 text-emerald-700 border border-emerald-200"
            }`}
            data-testid={`pricing-trial-${plan.id}`}
          >
            <Star size={9} fill="currentColor" />
            {plan.trialDays}-day free trial
          </div>
        )}

        {/* Price block. Two lines so the headline number stays huge
            and the secondary billing detail sits underneath. */}
        <div className="mt-5 flex items-end gap-1.5">
          <span className={`text-3xl font-extrabold tracking-tight tabular-nums ${
            popular ? "text-white" : "text-slate-900"
          }`}>
            {money(headlinePrice)}
          </span>
          <span className={`pb-1 text-xs font-medium ${
            popular ? "text-emerald-100/70" : "text-slate-500"
          }`}>
            /mo
          </span>
        </div>
        <div className={`mt-1 text-[11px] ${popular ? "text-emerald-100/60" : "text-slate-500"}`}>
          {cadence === "annual" ? (
            <>Billed <b>{money(plan.annual)}</b>/year · 2 months free</>
          ) : (
            <>Billed monthly · switch to annual anytime</>
          )}
        </div>

        {/* Primary CTA */}
        <button
          type="button"
          onClick={() => onSelect(plan)}
          disabled={loading || anyLoading}
          className={`mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-[13px] font-bold shadow-md transition-transform disabled:opacity-60 disabled:cursor-not-allowed ${
            !loading && !anyLoading ? "hover:shadow-lg hover:scale-[1.01]" : ""
          } ${
            popular
              ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
              : "bg-slate-900 text-white"
          }`}
          data-testid={`pricing-select-${plan.id}`}
        >
          {loading ? (
            <>
              <Loader2 size={13} className="animate-spin" /> Redirecting to Stripe…
            </>
          ) : wired && returning ? (
            <>{isPrevious ? (returning.canceled ? `Reactivate ${plan.name}` : `Keep ${plan.name}`) : `Switch to ${plan.name}`} <ArrowRight size={13} /></>
          ) : wired ? (
            <>Start {plan.trialDays}-day free trial <ArrowRight size={13} /></>
          ) : (
            <>Choose {plan.name} <ArrowRight size={13} /></>
          )}
        </button>
      </div>

      {/* Feature list — dark rule between price block and features
          so the eye reads them as a separate scan surface. */}
      <div className={`px-6 sm:px-7 pb-6 sm:pb-7 border-t ${
        popular ? "border-white/10" : "border-slate-100"
      } pt-5 flex-1 flex flex-col`}>
        <ul className="space-y-3">
          {plan.features.map((f, i) => {
            if (f.isSection) {
              return (
                <li key={i} className={`text-[11px] uppercase tracking-widest font-bold ${
                  popular ? "text-emerald-200" : "text-emerald-700"
                }`}>
                  {f.h}
                </li>
              );
            }
            return (
              <li key={i} className="flex items-start gap-2.5">
                <div className={`mt-0.5 shrink-0 w-4 h-4 rounded-full inline-flex items-center justify-center ${
                  popular ? "bg-emerald-400/20 text-emerald-300"
                          : "bg-emerald-100 text-emerald-600"
                }`}>
                  <Check size={11} strokeWidth={3} />
                </div>
                <div>
                  <div className={`text-[13px] font-semibold ${popular ? "text-white" : "text-slate-800"}`}>
                    {f.h}
                  </div>
                  {f.b && showDetail && (
                    <div className={`text-[11px] leading-relaxed mt-0.5 ${
                      popular ? "text-emerald-100/75" : "text-slate-500"
                    }`}>
                      {f.b}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {/* "Best for" footer — a soft italic tag that gives users a
            gut check without turning the card into a wall of copy. */}
        <div className={`mt-5 pt-4 border-t text-[11px] italic leading-relaxed ${
          popular
            ? "border-white/10 text-emerald-100/70"
            : "border-slate-100 text-slate-500"
        }`}>
          <b className="not-italic">Best for:</b> {plan.bestFor}
        </div>

        {/* Seat allowance — pinned to the bottom via `mt-auto` so it
            sits at the same y-position across all three cards even
            when feature lists differ in length. */}
        <div className={`mt-auto pt-5 text-[11px] uppercase tracking-widest font-semibold text-center ${
          popular ? "text-emerald-100/70" : "text-slate-500"
        }`}
        data-testid={`pricing-seat-copy-${plan.id}`}>
          {plan.seatCopy}
        </div>
      </div>
    </div>
  );
}



